/**
 * Wardrobe Feature API
 */

import { apiFetch, apiFormFetch, buildUrl, PROXY_BASE } from "@/shared/api/client";
import type { ApiResponse, UploadQuota } from "@/shared/api/types";
import type {
  WardrobeItem,
  WardrobeItemsParams,
  WardrobeItemsResponse,
  WardrobeStatsResponse,
  WardrobeOnboardResponse,
  WardrobeUploadJobResponse,
  AnalyzeForSetResponse,
  ConfirmOnboardJobResponse,
  Pod,
  PodWithItems,
  CatalogBasicsParams,
  CatalogBasicsResponse,
  CatalogCommitItem,
  CatalogCommitResponse,
} from "./types";

// ============================================
// Wardrobe Items
// ============================================

export async function getWardrobeItems(
  params?: WardrobeItemsParams
): Promise<ApiResponse<WardrobeItemsResponse>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/wardrobe`, params as Record<string, unknown>));
}

export async function getWardrobeStats(): Promise<ApiResponse<WardrobeStatsResponse>> {
  return apiFetch(`${PROXY_BASE}/wardrobe/stats`);
}

export async function getWardrobeItem(id: string): Promise<ApiResponse<WardrobeItem>> {
  return apiFetch(`${PROXY_BASE}/wardrobe/${id}`);
}

export async function deleteWardrobeItem(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
  return apiFetch(`${PROXY_BASE}/wardrobe/${id}`, {
    method: "DELETE",
  });
}

export async function updateWardrobeItem(
  id: string,
  data: { hasWorn?: boolean; isFavorite?: boolean }
): Promise<ApiResponse<WardrobeItem>> {
  return apiFetch(`${PROXY_BASE}/wardrobe/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// ============================================
// Wardrobe Onboarding
// ============================================

export async function onboardClothes(
  userId: string,
  image: File,
  wardrobeId?: string
): Promise<ApiResponse<WardrobeOnboardResponse>> {
  const formData = new FormData();
  formData.append("userId", userId);
  formData.append("image", image);
  if (wardrobeId) formData.append("wardrobeId", wardrobeId);

  return apiFormFetch(`${PROXY_BASE}/wardrobe/onboard`, formData);
}

// Vercel serverless functions have a 4.5MB request body limit. We aim to stay
// well under that accounting for multipart FormData overhead (~few KB per file).
const MAX_BATCH_BYTES = 3.8 * 1024 * 1024;

/**
 * Pack files into batches whose total size stays under Vercel's body limit.
 * Each returned batch is safe to POST to /wardrobe/onboard/batch.
 */
function packBatches(images: File[]): File[][] {
  const batches: File[][] = [];
  let current: File[] = [];
  let currentBytes = 0;
  for (const img of images) {
    if (img.size >= MAX_BATCH_BYTES) {
      // Oversized single file — send it alone and let the server decide.
      if (current.length) {
        batches.push(current);
        current = [];
        currentBytes = 0;
      }
      batches.push([img]);
      continue;
    }
    if (currentBytes + img.size > MAX_BATCH_BYTES && current.length > 0) {
      batches.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(img);
    currentBytes += img.size;
  }
  if (current.length) batches.push(current);
  return batches;
}

async function postBatchChunk(
  userId: string,
  images: File[],
  wardrobeId?: string
): Promise<ApiResponse<WardrobeUploadJobResponse>> {
  const formData = new FormData();
  formData.append("userId", userId);
  images.forEach((image) => {
    formData.append("images", image);
  });
  if (wardrobeId) formData.append("wardrobeId", wardrobeId);
  return apiFormFetch(`${PROXY_BASE}/wardrobe/onboard/batch`, formData);
}

export async function onboardClothesBatch(
  userId: string,
  images: File[],
  wardrobeId?: string
): Promise<ApiResponse<WardrobeUploadJobResponse>> {
  if (images.length > 10) {
    return {
      success: false,
      message: "Maximum 10 images allowed",
      error: "BATCH_LIMIT_EXCEEDED",
      statusCode: 400,
    } as ApiResponse<WardrobeUploadJobResponse>;
  }

  if (images.length === 0) {
    return {
      success: false,
      message: "At least one image is required",
      error: "NO_IMAGES_PROVIDED",
      statusCode: 400,
    } as ApiResponse<WardrobeUploadJobResponse>;
  }

  const batches = packBatches(images);

  // Fast path: single batch fits under the limit, post directly.
  if (batches.length === 1) {
    return postBatchChunk(userId, batches[0], wardrobeId);
  }

  // Multi-chunk: post sequentially. Return the FIRST successful response so the
  // existing WebSocket-based progress UI latches onto one job. Subsequent chunks
  // still produce their own upload jobs but arrive via the same WS channel.
  let firstResponse: ApiResponse<WardrobeUploadJobResponse> | null = null;
  let totalImages = 0;

  for (const chunk of batches) {
    const res = await postBatchChunk(userId, chunk, wardrobeId);
    if (!res.success) {
      // On failure, return what we've got so the UI can surface partial progress
      // or the error. If nothing succeeded yet, surface the failure directly.
      if (firstResponse) return firstResponse;
      return res;
    }
    if (!firstResponse) firstResponse = res;
    totalImages += chunk.length;
  }

  // Patch totalImages onto the first response so the progress indicator shows
  // the true total across all chunks.
  if (firstResponse?.success && firstResponse.data) {
    firstResponse.data.totalImages = totalImages;
  }
  return firstResponse!;
}

// ============================================
// Set Detection & Processing
// ============================================

export async function analyzeForSet(
  userId: string,
  image: File
): Promise<ApiResponse<AnalyzeForSetResponse>> {
  const formData = new FormData();
  formData.append("userId", userId);
  formData.append("image", image);

  return apiFormFetch(`${PROXY_BASE}/wardrobe/onboard/analyze`, formData);
}

export async function confirmOnboard(
  userId: string,
  analysisId: string,
  processAsSet: boolean
): Promise<ApiResponse<ConfirmOnboardJobResponse>> {
  return apiFetch(`${PROXY_BASE}/wardrobe/onboard/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId, analysisId, processAsSet }),
  });
}

// ============================================
// Upload Quota
// ============================================

export async function getUploadQuota(): Promise<ApiResponse<UploadQuota>> {
  return apiFetch(`${PROXY_BASE}/billing/upload-quota`);
}

// ============================================
// Pods
// ============================================

export async function getPods(): Promise<ApiResponse<Pod[]>> {
  return apiFetch(`${PROXY_BASE}/pods`);
}

export async function ensureDefaultPods(): Promise<ApiResponse<{ message: string }>> {
  return apiFetch(`${PROXY_BASE}/pods/ensure-defaults`, {
    method: "POST",
  });
}

export async function getPod(podName: string): Promise<ApiResponse<PodWithItems>> {
  return apiFetch(`${PROXY_BASE}/pods/name/${encodeURIComponent(podName)}`);
}

export async function createPod(name: string): Promise<ApiResponse<Pod>> {
  return apiFetch(`${PROXY_BASE}/pods`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export async function renamePod(podName: string, newName: string): Promise<ApiResponse<Pod>> {
  return apiFetch(`${PROXY_BASE}/pods/name/${encodeURIComponent(podName)}`, {
    method: "PUT",
    body: JSON.stringify({ newName }),
  });
}

export async function deletePod(podName: string): Promise<ApiResponse<{ message: string }>> {
  return apiFetch(`${PROXY_BASE}/pods/name/${encodeURIComponent(podName)}`, {
    method: "DELETE",
  });
}

export async function addItemToPod(podName: string, itemId: string): Promise<ApiResponse<{ message: string }>> {
  return apiFetch(`${PROXY_BASE}/pods/name/${encodeURIComponent(podName)}/items`, {
    method: "POST",
    body: JSON.stringify({ itemId }),
  });
}

export async function removeItemFromPod(podName: string, itemId: string): Promise<ApiResponse<{ message: string }>> {
  return apiFetch(`${PROXY_BASE}/pods/name/${encodeURIComponent(podName)}/items/${itemId}`, {
    method: "DELETE",
  });
}

export async function toggleFavorite(itemId: string): Promise<ApiResponse<{ isFavorited: boolean; message: string }>> {
  return apiFetch(`${PROXY_BASE}/pods/favorites/toggle`, {
    method: "POST",
    body: JSON.stringify({ itemId }),
  });
}

// ============================================
// Catalog Basics (third wardrobe-seeding entry point)
// ============================================

export async function getCatalogBasics(
  params?: CatalogBasicsParams
): Promise<ApiResponse<CatalogBasicsResponse>> {
  return apiFetch(buildUrl(`${PROXY_BASE}/wardrobe/catalog/basics`, params as Record<string, unknown>));
}

export async function commitCatalogSelection(
  items: CatalogCommitItem[]
): Promise<ApiResponse<CatalogCommitResponse>> {
  return apiFetch(`${PROXY_BASE}/wardrobe/catalog/commit`, {
    method: "POST",
    body: JSON.stringify({ items }),
  });
}
